import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LogIn, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Header } from '../components/Header';

const KundenLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kunden/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login fehlgeschlagen');
      }

      // Store token and user data
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_id', data.customer.id);
      localStorage.setItem('customer_email', data.customer.email);
      localStorage.setItem('customer_name', data.customer.name);

      // Redirect
      const redirect = searchParams.get('redirect') || '/kunden/dashboard';
      navigate(redirect);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border-2 border-gray-100 rounded-3xl p-10 shadow-xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Kunden-Login</h1>
              <p className="text-gray-600">Zugang zur Webakte</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">E-Mail</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                  placeholder="deine@email.de"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Passwort</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <Link 
                    to="/kunden/passwort-vergessen" 
                    className="text-sm text-brand-green hover:text-green-700 font-medium inline-flex items-center gap-1"
                    data-testid="forgot-password-link"
                  >
                    <KeyRound className="w-3.5 h-3.5" />
                    Passwort vergessen?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
                <LogIn className="w-5 h-5" />
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Noch keinen Zugang?{' '}
                <span className="text-gray-900 font-medium">Du erhältst eine Einladung per E-Mail.</span>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KundenLoginPage;
